import { PERMISSIONS } from './permissions.catalog';

/**
 * The rights each guarded destination opens on, any-of, mirroring the middleware the
 * service actually gates it with.
 *
 * One list per destination, shared by the route guard and by everything that links to it.
 * They were previously written out again at each site, and the copies drifted: a link kept
 * being drawn for a destination whose guard would only bounce the viewer to the dashboard.
 * A link is a promise that following it goes somewhere, so whatever decides the guard has
 * to be what decides the link.
 *
 * Read them with `anyVerdictFor` — `open` links, `locked` links to the plan placeholder,
 * `hidden` is not drawn at all.
 */

/** wallet `transactionsBasic` — navigate carries `Transactions` without `TransactionsBasic`. */
export const TRANSACTION_RIGHTS = [
  PERMISSIONS.wallet.TransactionsBasic,
  PERMISSIONS.wallet.Transactions,
] as const;

/** wallet `getBudgets` — either right opens the read side. */
export const BUDGET_RIGHTS = [
  PERMISSIONS.wallet.GetBudgets,
  PERMISSIONS.wallet.EditBudgets,
] as const;

/** wallet `anyWallet` — the Manage hub is nothing but wallet. */
export const WALLET_RIGHTS = [
  PERMISSIONS.wallet.TransactionsBasic,
  PERMISSIONS.wallet.Transactions,
  PERMISSIONS.wallet.Invoices,
  PERMISSIONS.wallet.Quotes,
  PERMISSIONS.wallet.GetBudgets,
  PERMISSIONS.wallet.EditBudgets,
] as const;

/** activity `projects` — every `/projects` route asks for this one right. */
export const PROJECTS_RIGHTS = [PERMISSIONS.activity.Projects] as const;

/** activity `externalTasks` — what `GET /tasks` clears. */
export const TASK_RIGHTS = [
  PERMISSIONS.activity.ExternalTasks,
  PERMISSIONS.activity.Tasks,
] as const;

/** The Maintain hub: activity's task queue above, vessel's equipment grid below. */
export const MAINTAIN_RIGHTS = [...TASK_RIGHTS, PERMISSIONS.vessel.Equipment] as const;

/** activity `maintenance` — Maintain's own two sections, Schedule and Service History. */
export const MAINTENANCE_RIGHTS = [PERMISSIONS.activity.Maintenance] as const;

/** wallet `invoices` — the only right the invoices list is gated on. */
export const INVOICE_RIGHTS = [PERMISSIONS.wallet.Invoices] as const;

/**
 * service-provider `basicServiceProvider`, which opens the provider and marina catalogues.
 * Navigate carries the internal right without the basic one, so naming only
 * `BasicServiceProvider` shut an owner out of a catalogue their plan pays for.
 */
export const CATALOGUE_RIGHTS = [
  PERMISSIONS['service-provider'].BasicServiceProvider,
  PERMISSIONS['service-provider'].ServiceProviderInternal,
] as const;

/** vessel `marina` reads: either right opens My marinas; `MarinaBooking` alone also writes. */
export const MARINA_RIGHTS = [
  PERMISSIONS.vessel.GetMarinaInfo,
  PERMISSIONS.vessel.MarinaBooking,
] as const;

/** The five logs the logbook opens on. */
export const LOGBOOK_RIGHTS = [
  PERMISSIONS.logbook.DailyLog,
  PERMISSIONS.logbook.DigitalNavigationLog,
  PERMISSIONS.logbook.TrashLog,
  PERMISSIONS.logbook.SafetyLog,
  PERMISSIONS.logbook.OilLog,
] as const;

/**
 * people `crewManagement` — the crew roster, and the only right its route guard names.
 *
 * A single right, but it belongs here for the same reason as the rest: the guard and every
 * link to it have to ask one question. Note that only `command_seat` carries it, so a
 * captain on a harbor or navigate plan does not hold it and the link is not drawn.
 */
export const CREW_MANAGEMENT_RIGHTS = [PERMISSIONS.users.CrewManagement] as const;

/**
 * The Manage hub: its money tiles open on wallet, its crew card on crew management and its
 * projects card on projects, so any one is a way in. Each card and tile stays gated on its own
 * right.
 */
export const MANAGE_RIGHTS = [
  ...WALLET_RIGHTS,
  ...CREW_MANAGEMENT_RIGHTS,
  ...PROJECTS_RIGHTS,
] as const;

/**
 * people's `checkManagePermissions` — the Roles & Permissions section of Settings.
 *
 * The service clears a permissions read or write for the vessel owner *or* for anyone
 * holding `ManagePermissions` (`internal/app/permissions/helpers.go`, and the same pair in
 * `people.UpdatePersonPermissions`), so the owner role is one of two ways in, not the gate.
 * `navigate_seat` and `command_seat` are the only seats carrying the right, so nobody on a
 * harbor plan arrives by right alone.
 */
export const MANAGE_PERMISSIONS_RIGHTS = [PERMISSIONS.vessel.ManagePermissions] as const;

/** activity `externalGallery` — either right opens the gallery; `InternalGallery` adds the vessel's. */
export const GALLERY_RIGHTS = [
  PERMISSIONS.activity.ExternalGallery,
  PERMISSIONS.activity.InternalGallery,
] as const;
